<script setup>
  import {computed, inject, onBeforeUnmount, onMounted, onUnmounted, onUpdated, ref} from "vue";

  import ComponentContactCard from "@components/Cards/ContactCard.vue";
  import ComponentContactCardFavorite from "@components/Cards/ContactCardFavorite.vue";
  import ComponentBmpShowDialog from "@components/Dialogs/BmpShowDialog.vue";
  import ComponentPatientAddressBookDialog from "@components/Dialogs/PatientAddressBookDialog.vue";
  import ComponentPatientAttributesDialog from "@components/Dialogs/PatientAttributesDialog.vue";
  import ComponentPatientContactsDialog from "@components/Dialogs/PatientContactsDialog.vue";
  import ComponentPatientMissingDataDialog from "@components/Dialogs/PatientMissingDataDialog.vue";
  import ComponentPatientNoteDialog from "@components/Dialogs/PatientNoteDialog.vue";
  import ComponentFoldable from "@components/Foldable/Foldable.vue";
  import ComponentPatientSidebarDegreeOfCareAttribute from "@components/Sidebars/PatientSidebar/DegreeOfCareAttribute.vue";
  import ComponentPatientSidebarMobilityAttribute from "@components/Sidebars/PatientSidebar/MobilityAttribute.vue";
  import ComponentPatientSidebarNursingHomeAttribute from "@components/Sidebars/PatientSidebar/NursingHomeAttribute.vue";
  import ComponentPatientSidebarPatientAddressAttribute from "@components/Sidebars/PatientSidebar/PatientAddressAttribute.vue";
  import ComponentPatientSidebarPatientinputFoldable from "@components/Sidebars/PatientSidebar/PatientinputFoldable.vue";
  import ComponentPatientSidebarSimpleAttribute from "@components/Sidebars/PatientSidebar/SimpleAttribute.vue";
  import ComponentPatientSidebarTubePatientAttribute from "@components/Sidebars/PatientSidebar/TubePatientAttribute.vue";
  import {router} from "@inertiajs/vue3";
  import {doctorProfession} from "@pages/Contact/Enums/Enums.js";
  import {patientGender, patientSalutation} from "@pages/Patients/Enums/Enums.js";
  import {MedBadge, MedButton, MedInfoButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentPatientSidebar",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      default: null,
    },
    transfer: {
      type: Object,
      default: null,
    },
    dynamicWidth: {
      type: Boolean,
      default: true,
    },
  });

  const privacy = inject("$privacy");
  const broadcast = inject("$broadcast");

  const refBmpShowDialog = ref(null);
  const refPatientAttributesDialog = ref(null);
  const refPatientAddressBookDialog = ref(null);
  const refPatientContactsDialog = ref(null);
  const refPatientNoteDialog = ref(null);
  const refPatientMissingDataDialog = ref(null);
  const refPatientSidebar = ref(null);

  const hidden = ref(false); // always visible by default (initially)

  const internalPatient = ref(props.patient);

  const doctorContactViaTransfer = computed(() => {
    if (
      props?.transfer?.data?.doctor && // << the addresses are nested in a "data" attribute (this is not a "Collection" data attribute)
      (props.transfer.data.doctor.first_name ||
        props.transfer.data.doctor.last_name ||
        props.transfer.data.doctor.company_name)
    ) {
      let contact = {
        // replace "Kardiologe" with "cardiologist" from enum
        profession:
          Object.keys(doctorProfession).find((key) => doctorProfession[key] === props.transfer.data.doctor.job_title) ||
          null,
        title: props.transfer.data.doctor.title ?? null,
        firstname: props.transfer.data.doctor.first_name ?? null,
        lastname: props.transfer.data.doctor.last_name ?? null,
        company: props.transfer.data.doctor.company_name ?? null,
        street: props.transfer.data.doctor.street_address ?? null,
        zip: props.transfer.data.doctor.postal_code ?? null,
        city: props.transfer.data.doctor.city ?? null,
        country: props.transfer.data.doctor.country ?? null,
        telephone: props.transfer.data.doctor.telephone ?? null,
        email: props.transfer.data.doctor.email ?? null,
      };

      if (internalPatient.value?.contacts?.length === 0) {
        // patient does not have any contacts yet, so
        // immediately return contact from transfer
        return contact;
      }

      if (
        internalPatient.value.contacts.some(
          (object) =>
            object.firstname === contact.firstname &&
            object.lastname === contact.lastname &&
            object.company === contact.company,
        )
      ) {
        // hide contact from transfer, if patient already contains #
        // a similar (likely the same) contact
        return null;
      }

      return contact;
    } else {
      return null;
    }
  });

  const handleResizeBreakpoint = (event) => {
    if (props.dynamicWidth === false) {
      // early exit when prop disables dynamic width of the sidebar
      return;
    }

    // otherwise we always "prefer" the sidebar to be open (initially)
    // and hide it, when the display is smaller than xl
    hidden.value = event.detail.breakpoint === "xl" || event.detail.breakpoint === "2xl" ? false : true;
  };

  onMounted(() => {
    globalThis.addEventListener("breakpoint", handleResizeBreakpoint);

    broadcast.patient.onMessage(internalPatient.value.id, handleBroadcastMessage);
    addInteractionListener();

    // immediately open dialog when required patient attributes are not set
    if (
      internalPatient.value.firstname === null ||
      internalPatient.value.lastname === null ||
      internalPatient.value.birthdate === null ||
      internalPatient.value.gender === null
    ) {
      openPatientMissingDataDialog();
    }
  });

  onUpdated(() => {
    privacy.decryptStaticContent(refPatientSidebar.value);
  });

  onBeforeUnmount(() => {
    broadcast.patient.offMessage(internalPatient.value.id, handleBroadcastMessage);
  });

  onUnmounted(() => {
    globalThis.removeEventListener("breakpoint", handleResizeBreakpoint);
  });

  function openPatientNoteDialog() {
    refPatientNoteDialog.value.open(internalPatient.value);
  }

  function openPatientMissingDataDialog() {
    refPatientMissingDataDialog.value.open();
  }

  function getComponentName(type) {
    switch (type) {
      case "degreeOfCare": {
        return ComponentPatientSidebarDegreeOfCareAttribute;
      }
      case "mobility": {
        return ComponentPatientSidebarMobilityAttribute;
      }
      case "nursingHome": {
        return ComponentPatientSidebarNursingHomeAttribute;
      }
      case "patientAddress": {
        return ComponentPatientSidebarPatientAddressAttribute;
      }
      case "tubePatient": {
        return ComponentPatientSidebarTubePatientAttribute;
      }
      case "insulinPump":
      case "palliativePatient":
      case "port":
      case "stoma": {
        return ComponentPatientSidebarSimpleAttribute;
      }
    }
  }

  function addInteractionListener() {
    if (refPatientSidebar.value) {
      refPatientSidebar.value.addEventListener("mouseenter", handleInteractWithSidebar);
    }
  }

  function removeInteractionListener() {
    if (refPatientSidebar.value) {
      refPatientSidebar.value.removeEventListener("mouseenter", handleInteractWithSidebar);
    }
  }

  const handleBroadcastMessage = (data) => {
    switch (data?.action) {
      case "reload.patient": {
        router.reload({
          only: ["patient"],
          preserveState: true,
          preserveScroll: true,
          onSuccess: (result) => {
            internalPatient.value = result.props.patient;
            removeInteractionListener();

            // the patient sidebar is a kind of "hub" for patient data; all
            // "reload.patient" events are handled here. so the sidebar
            // itself triggers an event after the reload to other components
            // (to avoid parallel/subsequent reload requests)
            broadcast.patient.postMessage(internalPatient.value.id, {
              action: "refreshAfterReload.patient",
            });
          },
        });

        break;
      }
    }
  };

  const handleInteractWithSidebar = () => {
    router.reload({
      only: ["patient"],
      onSuccess: (result) => {
        internalPatient.value = result.props.patient;
        removeInteractionListener();
      },
    });
  };

  const openPatientAddressBookViaContactsDialog = (searchContact, pendingContact = null) => {
    refPatientContactsDialog.value.searchInAddressBook(searchContact, pendingContact);
  };

  const openPatientContactsDialog = (pendingContact = null) => {
    refPatientContactsDialog.value.open(pendingContact);
  };

  const openPatientAttributesDialog = () => {
    refPatientAttributesDialog.value.open();
  };

  const toggleHidden = () => {
    // when the user has changed the hidden status manually
    // we do not listen on breakpoint events (window
    // resize events) anymore.
    globalThis.removeEventListener("breakpoint", handleResizeBreakpoint);

    hidden.value = !hidden.value;
  };
</script>

<template>
  <aside
    ref="refPatientSidebar"
    :class="
      cn('relative bg-white px-4 shadow-md transition-all', {'-ml-4 w-12 pt-12 pb-10': hidden, 'w-64 pt-4': !hidden})
    "
    :data-test="getTestId('patient-sidebar')"
  >
    <MedButton
      :aria-label="hidden ? 'Patienten Sidebar einblenden' : 'Patienten Sidebar ausblenden'"
      :aria-expanded="!hidden"
      icon="arrow_forward"
      rounded="full"
      size="sm"
      variant="primary"
      :class="
        cn('absolute top-2', {
          'right-1.5': hidden,
          '-right-4': !hidden,
        })
      "
      :icon-class="cn('transition-transform duration-200', {'rotate-180': !hidden})"
      :data-test="getTestId('patient-sidebar-toggle-hidden-button')"
      @click="toggleHidden()"
    />

    <div v-if="hidden" class="absolute right-1.5 bottom-1">
      <MedTooltip :content="patient.note === null ? 'Patientennotiz anlegen' : 'Patientennotiz öffnen'" side="right">
        <div class="relative">
          <MedBadge
            variant="secondary"
            :dot="patient.note !== null"
            :data-test="getTestId('patient-sidebar-note-badge')"
          >
            <MedButton
              aria-label="Patientennotiz öffnen"
              flat
              variant="primary"
              icon="assignment"
              size="sm"
              rounded="full"
              :data-test="getTestId('patient-sidebar-open-note-button')"
              @click="openPatientNoteDialog()"
            />
          </MedBadge>
        </div>
      </MedTooltip>
    </div>

    <h1
      class="text-xl font-semibold"
      :class="{
        '-ml-2 max-h-120 w-6 translate-x-2 rotate-180 truncate whitespace-nowrap [writing-mode:vertical-lr]': hidden,
      }"
    >
      <component
        :is="hidden ? 'button' : 'span'"
        class="text-gray-900 hover:no-underline enabled:cursor-pointer"
        :test-id="getTestId(`patient-sidebar-toggle-hidden-${hidden ? 'button' : 'span'}`)"
        @click="
          () => {
            if (hidden) hidden = false;
          }
        "
      >
        <span v-if="internalPatient.salutation" class="text-base font-normal">
          {{ patientSalutation[internalPatient.salutation] || "" }}
        </span>

        <span v-if="internalPatient.title" class="text-base font-normal" v-text="internalPatient.title" />
        <br v-if="!hidden && (internalPatient.salutation || internalPatient.title)" />
        <span :data-encrypted="internalPatient.firstname" />
        <span :data-encrypted="internalPatient.suffix" />
        <span :data-encrypted="internalPatient.prefix" />
        <span :data-encrypted="internalPatient.lastname" />
      </component>

      <br v-if="internalPatient.is_fake && !hidden" />

      <MedTooltip
        v-if="internalPatient.is_fake && internalPatient.aliasname"
        content="Alias"
        :disabled="hidden"
        side="right"
      >
        <span class="text-base font-normal">„{{ internalPatient.aliasname }}“</span>
      </MedTooltip>
    </h1>

    <div v-if="!hidden" class="space-y-2">
      <div class="text-sm">
        <span v-if="internalPatient.birthdate" class="after:content-[',_'] last:after:content-['']">
          <template v-if="internalPatient.is_fake">
            <span data-format="age" :data-encrypted="internalPatient.birthdate" />
          </template>

          <template v-else>
            <span data-format="date" :data-encrypted="internalPatient.birthdate" />
            <span data-format="age-brackets" :data-encrypted="internalPatient.birthdate" />
          </template>
        </span>

        <span
          v-if="internalPatient.gender"
          class="after:content-[',_'] last:after:content-['']"
          v-text="patientGender[internalPatient.gender] || ''"
        />

        <span
          v-if="internalPatient.insurancenumber"
          class="after:content-[',_'] last:after:content-['']"
          :data-encrypted="internalPatient.insurancenumber"
        />
      </div>

      <div v-if="internalPatient.is_fake && internalPatient?.shared_owners" class="mt-2 mb-4 text-sm leading-tight">
        <h2 class="text-gray-600">Patient wurde geteilt von</h2>
        <span class="font-semibold">{{ internalPatient?.shared_owners[0]?.company }}</span>
        <span v-if="internalPatient?.shared_owners[0]?.name" class="text-sm">
          ({{ internalPatient?.shared_owners[0]?.name }})
        </span>
      </div>

      <MedBadge
        variant="secondary"
        :dot="patient.note !== null"
        class="-top-0.5 right-35"
        :data-test="getTestId('patient-sidebar-note-badge')"
      >
        <MedLink
          as="button"
          variant="primary"
          size="sm"
          icon="assignment"
          class="leading-tight"
          :data-test="getTestId('patient-sidebar-open-note-link')"
          @click.prevent="openPatientNoteDialog()"
        >
          {{ patient.note === null ? "Patientennotiz anlegen" : "Patientennotiz öffnen" }}
        </MedLink>
      </MedBadge>

      <template v-if="patient.mp_date && $page.props.client.license.is_pro">
        <MedLink
          as="button"
          variant="primary"
          icon="qr_code_scanner"
          size="sm"
          class="leading-tight"
          :data-test="getTestId('patient-sidebar-open-bmp-link')"
          @click.prevent="$refs.refBmpShowDialog.open()"
        >
          Gespeicherter BMP
        </MedLink>
      </template>

      <MedLink
        as="inertia"
        variant="primary"
        icon="edit"
        size="sm"
        class="leading-tight"
        :href="$route('patients.edit', {id: internalPatient.id})"
        :data-test="getTestId('patient-sidebar-edit-inertia-link')"
      >
        Patienten bearbeiten
      </MedLink>

      <ComponentFoldable
        class="mt-4"
        title="Individuelle Besonderheiten"
        :test-id="getTestId('patient-sidebar-attributes')"
        :badge-label="internalPatient?.patient_attributes?.length || 0"
        :badge-color="internalPatient?.patient_attributes?.length > 0 ? 'blue' : 'gray'"
      >
        <div v-if="internalPatient?.patient_attributes?.length">
          <ul class="space-y-4">
            <li v-for="attribute in internalPatient.patient_attributes" :key="attribute.id">
              <component :is="getComponentName(attribute.type)" :attribute="attribute" :patient="internalPatient" />
            </li>
          </ul>
        </div>

        <div v-else class="text-sm text-gray-500">
          Erfassen Sie zusätzliche Angaben und medizinische Besonderheiten zu diesem Patienten.
        </div>

        <MedLink
          as="button"
          variant="primary"
          icon="edit"
          size="sm"
          class="mt-2 leading-tight"
          :data-test="getTestId('patient-sidebar-open-attributes-foldable-link')"
          @click.prevent="openPatientAttributesDialog()"
        >
          Besonderheiten bearbeiten
        </MedLink>
      </ComponentFoldable>

      <ComponentFoldable
        v-if="!internalPatient.is_fake"
        title="Kontakte"
        :test-id="getTestId('patient-sidebar-contacts')"
        :badge-label="internalPatient?.contacts?.length + (doctorContactViaTransfer ? 1 : 0) || 0"
        :badge-color="internalPatient?.contacts?.length + (doctorContactViaTransfer ? 1 : 0) > 0 ? 'blue' : 'gray'"
      >
        <div v-if="internalPatient?.contacts?.length || doctorContactViaTransfer">
          <ol class="space-y-4">
            <li v-if="doctorContactViaTransfer" class="relative bg-mcred-50">
              <div class="px-2 pt-2">
                <div class="flex items-center space-x-2">
                  <h6 class="text-xs">
                    <MedLink
                      as="button"
                      variant="primary"
                      :data-test="getTestId('patient-sidebar-contacts-foldable-wawi-link')"
                      @click.prevent="
                        openPatientAddressBookViaContactsDialog(doctorContactViaTransfer, doctorContactViaTransfer)
                      "
                    >
                      Kontakt aus WaWi
                    </MedLink>
                  </h6>

                  <MedInfoButton
                    size="md"
                    side="right"
                    aria-label="WaWi Kontakt Info"
                    :data-test="getTestId('patient-sidebar-wawi-info-button')"
                  >
                    <p class="text-sm">
                      Der folgende Kontakt wurde aus der Warenwirtschaft übertragen und ist nur in dieser Analyse
                      verfügbar. Sie können den Kontakt übergreifend zu Ihrem Patienten speichern.
                    </p>
                  </MedInfoButton>
                </div>
              </div>

              <ComponentContactCard :contact="doctorContactViaTransfer" :small="true" />

              <MedButton
                aria-label="Kontakt hinzufügen"
                variant="primary"
                icon="add"
                rounded="full"
                class="absolute right-2 bottom-2"
                :data-test="getTestId('patient-sidebar-contacts-foldable-open-address-dialog-button')"
                @click="openPatientAddressBookViaContactsDialog(doctorContactViaTransfer, doctorContactViaTransfer)"
              />
            </li>

            <li v-for="contact in internalPatient.contacts" :key="contact.id" class="relative">
              <ComponentContactCard :contact="contact" :small="true" />
              <ComponentContactCardFavorite :contact="contact" :patient="internalPatient" />
            </li>
          </ol>
        </div>

        <div v-else class="text-sm text-gray-500">
          Verknüpfen Sie Kontakte (Ärzte / Pflegedienste / Heime) mit diesem Patienten.
        </div>

        <MedLink
          as="button"
          variant="primary"
          icon="edit"
          size="sm"
          class="mt-2 leading-tight"
          :data-test="getTestId('patient-sidebar-contacts-foldable-open-contacts-dialog-link')"
          @click.prevent="openPatientContactsDialog(doctorContactViaTransfer ?? null)"
        >
          Kontakte bearbeiten
        </MedLink>
      </ComponentFoldable>

      <ComponentPatientSidebarPatientinputFoldable
        v-if="record"
        :patient="internalPatient"
        :record="record"
        :test-id="getTestId('patient-sidebar')"
        @open:address-book="(data) => openPatientAddressBookViaContactsDialog(data.search)"
      />
    </div>
  </aside>

  <ComponentPatientNoteDialog ref="refPatientNoteDialog" />
  <ComponentPatientMissingDataDialog ref="refPatientMissingDataDialog" :test-id="getTestId('patient-sidebar')" />

  <ComponentPatientAttributesDialog
    ref="refPatientAttributesDialog"
    :patient="internalPatient"
    :test-id="getTestId('patient-sidebar')"
  />

  <ComponentPatientContactsDialog
    ref="refPatientContactsDialog"
    :patient="internalPatient"
    :test-id="getTestId('patient-sidebar')"
  />

  <ComponentPatientAddressBookDialog ref="refPatientAddressBookDialog" :patient="internalPatient" />

  <ComponentBmpShowDialog ref="refBmpShowDialog" :patient="internalPatient" />
</template>
